import mixpanel from 'mixpanel-browser';
import { ref } from 'vue';

/**
 * A Vue hook for Mixpanel analytics integration.
 * Provides methods for tracking events, identifying users, and managing the Mixpanel instance.
 *
 * @returns {Object} Mixpanel utility methods
 */
export default function useMixpanel() {
  const isInitialized = ref(false);
  const token = import.meta.env.MIX_MIXPANEL_TOKEN;
  const debug = import.meta.env.MIX_MIXPANEL_DEBUG === 'true';

  /**
   * Initialize the Mixpanel instance if not already initialized
   */
  const initialize = () => {
    if (!token) {
      console.warn('Mixpanel token is missing. Tracking will be disabled.');
      return false;
    }

    if (!isInitialized.value) {
      try {
        mixpanel.init(token, {
          debug,
          track_pageview: 'full-url', // Track any URL changes in the path, query string, or hash
          persistence: 'localStorage',
          upgrade: true,
          ignore_dnt: false, // Honor Do Not Track settings by default
        });
        isInitialized.value = true;
        return true;
      } catch (error) {
        console.error('Failed to initialize Mixpanel:', error);
        return false;
      }
    }
    return isInitialized.value;
  };

  // Initialize on first import
  initialize();

  /**
   * Identify a user in Mixpanel
   *
   * @param {string} userId - Unique identifier for the user
   * @param {Object} [properties={}] - User properties to record
   * @returns {Promise<Object>} Result of the identification
   */
  const identify = async (userId, properties = {}) => {
    if (!initialize()) {
      return { identified: false, reason: 'not_initialized' };
    }

    try {
      mixpanel.identify(userId);

      if (Object.keys(properties).length > 0) {
        mixpanel.people.set(properties);
      }

      return { identified: true, userId, properties };
    } catch (error) {
      console.error('Mixpanel identify error:', error);
      return { identified: false, error };
    }
  };

  /**
   * Track an event in Mixpanel
   *
   * @param {string} event - Name of the event to track
   * @param {Object} [properties={}] - Properties associated with the event
   * @returns {Promise<Object>} Result of the tracking
   */
  const track = async (event, properties = {}) => {
    if (!event) {
      console.error('Event name is required for tracking');
      return { tracked: false, reason: 'missing_event_name' };
    }

    if (!initialize()) {
      return { tracked: false, reason: 'not_initialized' };
    }

    try {
      // Dispatch a custom browser event for any local listeners
      const customEvent = new CustomEvent(`mixpanel:${event}`, {
        detail: properties,
        bubbles: true
      });

      document.dispatchEvent(customEvent);

      // Track in Mixpanel
      mixpanel.track(event, properties);

      return { tracked: true, event, properties };
    } catch (error) {
      console.error('Mixpanel track error:', error);
      return { tracked: false, event, error };
    }
  };

  /**
   * Reset the current user's identity and clear their properties
   *
   * @returns {Promise<Object>} Result of the reset operation
   */
  const reset = async () => {
    if (!initialize()) {
      return { reset: false, reason: 'not_initialized' };
    }

    try {
      mixpanel.reset();
      return { reset: true };
    } catch (error) {
      console.error('Mixpanel reset error:', error);
      return { reset: false, error };
    }
  };

  /**
   * Register persistent properties to be included with all events
   *
   * @param {Object} properties - Properties to register
   * @returns {Object} Result of the register operation
   */
  const registerProperties = (properties = {}) => {
    if (!initialize()) {
      return { registered: false, reason: 'not_initialized' };
    }

    try {
      mixpanel.register(properties);
      return { registered: true, properties };
    } catch (error) {
      console.error('Mixpanel register error:', error);
      return { registered: false, error };
    }
  };

  /**
   * Track a page view event
   *
   * @param {Object} [properties={}] - Additional properties to include
   * @returns {Promise<Object>} Result of the tracking
   */
  const trackPageView = async (properties = {}) => {
    const url = window?.location?.href || '';
    const title = document?.title || '';

    return track('Page View', {
      url,
      title,
      ...properties,
    });
  };

  return {
    identify,
    track,
    reset,
    trackPageView,
    registerProperties,
    isInitialized: () => isInitialized.value
  };
}
